'use client'

import { useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useSelectCheapestQuoteMutation } from '@b-stock/fe-core/bstock-next/mutations'
import type { OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import {
  getSelectedQuoteQuery,
  locationEligibilityQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import type { LocationModel } from '@b-stock/location-api-client'
import { ShipmentType } from '@b-stock/shipment-api-client'

type UsePopupAutoQuoteGenerationProps = {
  listingId: string
  accountId: string | undefined
  personId: string | undefined
  isQuoteFetched: boolean
  selectedQuote: OrchestratedQuote | null | undefined
  defaultLocation: LocationModel | undefined
  isLocationFetched: boolean
}

/**
 * Drives auto-quote generation for the ManifestSummaryPopup.
 *
 * When getSelectedQuoteQuery returns null (no previously selected quote), this
 * hook checks location eligibility and, if eligible, calls
 * useSelectCheapestQuoteMutation to generate and select the cheapest quote on
 * the backend.  It seeds the React Query cache directly so selectedQuote
 * updates in the popup without an extra network round-trip.
 *
 * Returns isAutoGenerating=true while any part of this process is in flight,
 * which the popup adds to its isPopupLoading guard.
 */
const usePopupAutoQuoteGeneration = ({
  listingId,
  accountId,
  personId,
  isQuoteFetched,
  selectedQuote,
  defaultLocation,
  isLocationFetched,
}: UsePopupAutoQuoteGenerationProps): { isAutoGenerating: boolean } => {
  const [autoGenComplete, setAutoGenComplete] = useState(false)
  const hasAttempted = useRef(false)

  // Allows re-generation if the component is reused with a different listing.
  useEffect(() => {
    hasAttempted.current = false
    setAutoGenComplete(false)
  }, [listingId])

  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  const { mutateAsync: generateQuote } = useSelectCheapestQuoteMutation()

  const quoteNeedsGeneration = !!personId && isQuoteFetched && !selectedQuote

  const eligibility = locationEligibilityQuery.useWithOpts(
    [{ listingId, locationId: [defaultLocation?.id ?? ''] }],
    { enabled: quoteNeedsGeneration && !!defaultLocation && !autoGenComplete }
  )
  const isEligible = eligibility.data?.[0]?.isEligible ?? false

  // No default deliverable location → cannot generate a quote
  useEffect(() => {
    if (quoteNeedsGeneration && !defaultLocation && isLocationFetched) {
      setAutoGenComplete(true)
    }
  }, [quoteNeedsGeneration, defaultLocation, isLocationFetched])

  // Destination location is not eligible for B-Stock shipping
  useEffect(() => {
    if (quoteNeedsGeneration && eligibility.isFetched && !isEligible) {
      setAutoGenComplete(true)
    }
  }, [quoteNeedsGeneration, eligibility.isFetched, isEligible])

  const shouldGenerate =
    quoteNeedsGeneration &&
    !!defaultLocation &&
    eligibility.isFetched &&
    isEligible &&
    !autoGenComplete

  useEffect(() => {
    if (!shouldGenerate || hasAttempted.current) return
    hasAttempted.current = true

    void generateQuote({
      quoteOptions: {
        listingId,
        destinationLocationId: defaultLocation.id,
        accountId,
        shipmentType: ShipmentType.Binding,
      },
      listingId,
    })
      .then((quoteDto) => {
        // Seed the cache so selectedQuote in the popup updates immediately.
        // defaultLocation is captured from the render where shouldGenerate first became true.
        // origin/destination are display-only placeholders — the popup only uses totalPrice.
        const seedQuote: OrchestratedQuote = {
          ...quoteDto,
          origin: defaultLocation,
          destination: defaultLocation,
        }
        queryClient.setQueryData(
          getSelectedQuoteQuery.getKey(accessToken, [listingId, accountId]),
          seedQuote
        )
        setAutoGenComplete(true)
      })
      .catch(() => {
        setAutoGenComplete(true)
      })
    // Intentionally reduced dep array: only re-run when shouldGenerate flips
    // oxlint-disable-next-line react/exhaustive-deps
  }, [shouldGenerate])

  // True from the moment we know a quote is needed + destination is available,
  // all the way through eligibility check and mutation, until autoGenComplete.
  // This prevents any flash of "—" content before generation finishes.
  const isAutoGenerating =
    !autoGenComplete && quoteNeedsGeneration && !!defaultLocation

  return { isAutoGenerating }
}

export default usePopupAutoQuoteGeneration
